/**
 * Centralized error handling utilities for SmartSend v2
 * Provides consistent error logging, toast notifications, and error formatting
 */

import React from 'react';
import { toast } from 'react-toastify';
import ErrorMessageWithSupport from '../components/ErrorMessageWithSupport';
import { SUPPORT_EMAIL, createSupportPattern } from './supportEmail';

// ============================================
// ERROR TYPES
// ============================================

export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  API: 'API_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTH: 'AUTH_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

// ============================================
// ERROR SEVERITY LEVELS
// ============================================

export const ERROR_SEVERITY = {
  LOW: 'low',       // Minor issues, user can continue
  MEDIUM: 'medium', // Notable issues, user should be aware
  HIGH: 'high',     // Critical issues, blocks user progress
  CRITICAL: 'critical', // System failures
};

// ============================================
// ERROR LOGGING
// ============================================

/**
 * Log error with context and severity
 * @param {Error|string} error - Error object or message
 * @param {Object} context - Additional context (component, action, etc.)
 * @param {string} severity - Error severity level
 */
export const logError = (error, context = {}, severity = ERROR_SEVERITY.MEDIUM) => {
  const timestamp = new Date().toISOString();
  const errorMessage = error instanceof Error ? error.message : error;
  const errorStack = error instanceof Error ? error.stack : undefined;

  const logEntry = {
    timestamp,
    severity,
    message: errorMessage,
    context,
    stack: errorStack,
  };

  // Log based on severity
  switch (severity) {
    case ERROR_SEVERITY.CRITICAL:
    case ERROR_SEVERITY.HIGH:
      console.error('❌ [SmartSend Error]', logEntry);
      break;
    case ERROR_SEVERITY.MEDIUM:
      console.warn('⚠️  [SmartSend Warning]', logEntry);
      break;
    case ERROR_SEVERITY.LOW:
    default:
      console.log('ℹ️  [SmartSend Info]', logEntry);
  }

  // In production, you might want to send to error tracking service
  // import { isDevelopment } from '../env/config';
  // if (!isDevelopment()) {
  //   // Send to Sentry, LogRocket, etc.
  // }

  return logEntry;
};

// ============================================
// ERROR CLASSIFICATION
// ============================================

/**
 * Classify error based on error object or response
 * @param {Error|Object} error - Error object or API response
 * @returns {string} Error type
 */
export const classifyError = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;

  // Network errors
  if (error.message === 'Network request failed' ||
    error.message === 'Failed to fetch' ||
    error.name === 'NetworkError') {
    return ERROR_TYPES.NETWORK;
  }

  // Timeout errors
  if (error.name === 'TimeoutError' ||
    error.message?.includes('timeout')) {
    return ERROR_TYPES.TIMEOUT;
  }

  // Auth errors
  if (error.code === 'TOKEN_EXPIRED' ||
    error.code === 'IDENTITY_MISSING' ||
    error.status === 401 ||
    error.status === 403) {
    return ERROR_TYPES.AUTH;
  }

  // Validation errors
  if (error.code === 'VALIDATION_ERROR' ||
    error.code === 'RECIPIENT_INFO_REQUIRED' ||
    error.status === 400 ||
    error.status === 422) {
    return ERROR_TYPES.VALIDATION;
  }

  // API errors
  if (error.status >= 400 && error.status < 600) {
    return ERROR_TYPES.API;
  }

  return ERROR_TYPES.UNKNOWN;
};

// ============================================
// USER-FRIENDLY ERROR MESSAGES
// ============================================

/**
 * Convert technical error to user-friendly message
 * @param {Error|Object} error - Error object
 * @param {string} defaultMessage - Fallback message
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyMessage = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  const errorType = classifyError(error);

  switch (errorType) {
    case ERROR_TYPES.NETWORK:
      return 'Network connection issue. Please check your internet and try again.';

    case ERROR_TYPES.TIMEOUT:
      return 'Request timed out. Please try again.';

    case ERROR_TYPES.AUTH:
      return 'Your session has expired. Please refresh the page.';

    case ERROR_TYPES.VALIDATION:
      // Use specific message from error if available
      return error.message || error.error || 'Please check your information and try again.';

    case ERROR_TYPES.API:
      // Use specific message from error if available
      return error.message || error.error || 'Server error. Please try again later.';

    default:
      return defaultMessage;
  }
};

// ============================================
// TOAST NOTIFICATIONS
// ============================================

/**
 * Show error toast with consistent formatting
 * 
 * @note This function uses React JSX and should only be called from React components or contexts where React is available.
 * 
 * @param {Error|string} error - Error object or message
 * @param {Object} context - Additional context
 * @param {Object} options - Toast options
 * @param {Object} errorData - Error data for support email (optional). When provided, enables "Contact Support" link in the error toast that opens a pre-filled email with order and error information.
 * @param {string} errorData.orderId - Order ID
 * @param {Object} errorData.orderData - Order data object
 * @param {string} errorData.errorCode - Error code from backend
 * @param {string} errorData.phoneNumber - User's phone number
 * @param {Object} errorData.address - Address object
 * @param {string} errorData.context - Additional context about where the error occurred
 */
export const showErrorToast = (error, context = {}, options = {}, errorData = {}) => {
  const message = typeof error === 'string'
    ? error
    : getUserFriendlyMessage(error);

  const severity = classifyError(error);
  const isHighPriority = severity === ERROR_SEVERITY.HIGH || severity === ERROR_SEVERITY.CRITICAL;

  // Log the error
  logError(error, context, isHighPriority ? ERROR_SEVERITY.HIGH : ERROR_SEVERITY.MEDIUM);

  // Check if errorData is provided and valid (backend errors)
  const hasErrorData = errorData && typeof errorData === 'object' && Object.keys(errorData).length > 0;

  // Check if message already contains support text to avoid duplication (case-insensitive)
  // Use regex pattern matching for consistency with ErrorMessageWithSupport component
  // and to avoid false positives from substring matches
  const { supportPattern } = createSupportPattern();
  const hasSupportText = supportPattern.test(message);

  // Add support email to message if errorData is provided and message doesn't already contain it
  // The email address will be automatically detected and made clickable by ErrorMessageWithSupport
  const messageWithSupport = hasErrorData && !hasSupportText
    ? `${message} Please contact ${SUPPORT_EMAIL} if you need some help.`
    : message;

  // Prepare error data for support email
  // Extract error code with fallback logic for better readability
  const errorCodeFromError = typeof error === 'object' && error !== null ? error.code : undefined;
  const errorCode = errorData.errorCode || errorCodeFromError;
  
  const supportErrorData = hasErrorData ? {
    orderId: errorData.orderId,
    orderData: errorData.orderData,
    errorMessage: message,
    errorCode: errorCode,
    phoneNumber: errorData.phoneNumber,
    address: errorData.address,
    context: errorData.context || context.component || 'Unknown',
  } : {};

  // Render toast with support link if errorData is provided
  const toastContent = hasErrorData
    ? <ErrorMessageWithSupport message={messageWithSupport} errorData={supportErrorData} />
    : messageWithSupport;

  // Show toast
  // If support link is present (hasErrorData) or error is high priority, use longer duration
  // to ensure users have adequate time to interact, especially important for users with motor disabilities
  // Use 15 seconds instead of infinite to prevent toast overflow while maintaining accessibility
  // This duration provides sufficient time for interaction while preventing UI clutter from multiple toasts
  const shouldAutoClose = (hasErrorData || isHighPriority) ? 15000 : 5000;

  toast.error(toastContent, {
    autoClose: shouldAutoClose,
    position: 'top-center',
    ...options,
  });
};

/**
 * Show success toast
 * @param {string} message - Success message
 * @param {Object} options - Toast options
 */
export const showSuccessToast = (message, options = {}) => {
  toast.success(message, {
    autoClose: 3000,
    position: 'top-center',
    ...options,
  });
};

/**
 * Show info toast
 * @param {string} message - Info message
 * @param {Object} options - Toast options
 */
export const showInfoToast = (message, options = {}) => {
  toast.info(message, {
    autoClose: 4000,
    position: 'top-center',
    ...options,
  });
};

/**
 * Show warning toast
 * @param {string} message - Warning message
 * @param {Object} options - Toast options
 */
export const showWarningToast = (message, options = {}) => {
  toast.warn(message, {
    autoClose: 4000,
    position: 'top-center',
    ...options,
  });
};

// ============================================
// ERROR HANDLING WRAPPER
// ============================================

/**
 * Wrap async function with standardized error handling
 * @param {Function} fn - Async function to wrap
 * @param {Object} context - Context for error logging
 * @param {Object} options - Options (showToast, logError)
 * @returns {Function} Wrapped function
 */
export const withErrorHandler = (fn, context = {}, options = {}) => {
  const { showToast = true, shouldLog = true } = options;

  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (shouldLog) {
        logError(error, context, ERROR_SEVERITY.HIGH);
      }

      if (showToast) {
        showErrorToast(error, context);
      }

      // Re-throw for caller to handle if needed
      throw error;
    }
  };
};

// ============================================
// API ERROR HANDLER
// ============================================

/**
 * Handle API response errors consistently
 * @param {Object} response - API response
 * @param {Object} context - Context for logging
 * @returns {Object} Standardized error object
 */
export const handleAPIError = (response, context = {}) => {
  const errorType = classifyError(response);
  const message = getUserFriendlyMessage(response);

  const error = {
    type: errorType,
    message,
    code: response.code,
    status: response.status,
    data: response.data || response,
  };

  // Log the error
  logError(error, { ...context, response }, ERROR_SEVERITY.MEDIUM);

  return error;
};

// ============================================
// VALIDATION ERROR HELPERS
// ============================================

/**
 * Format validation errors for display
 * @param {Object} errors - Validation errors object
 * @returns {string} Formatted error message
 */
export const formatValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return 'Please check your information and try again.';
  }

  const errorMessages = Object.entries(errors)
    .map(([field, message]) => {
      const fieldName = field.replace(/([A-Z])/g, ' $1').trim();
      const capitalizedField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
      return `${capitalizedField}: ${message}`;
    })
    .join('\n');

  return errorMessages || 'Please check your information and try again.';
};

